// Okna B2B: nová a upravená firma, změna stavu, další krok, osoba (fáze 2).
// Změna stavu se vždy potvrzuje (ConfirmDialog) a zapíše se do časové osy.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ArrowRightLeft, CalendarClock, UserPlus, Search, Loader2, Trash2 } from "lucide-react";
import { Modal, HlavickaOkna, Field, Hlaska, RozbalovaciVyber } from "../ui.jsx";
import DatePicker from "../DatePicker.jsx";
import { toast } from "../Toast.jsx";
import { confirmDialog } from "../confirmHost.jsx";
import { b2bGet, b2bPost } from "./b2bApi.js";
import { STAVY, SEGMENTY, ZDROJE, VELIKOSTI, TYPY_KROKU, dnesIsoPraha, dmr } from "./b2bUi.jsx";

const items = (obj, popis = (v) => v.label ?? v) => Object.entries(obj).map(([k, v]) => ({ key: k, label: popis(v) }));

/* --- Nová a upravená firma ---------------------------------------------- */
const PRAZDNA = { nazev: "", ico: "", dic: "", adresa: "", mesto: "", region: "", web: "", telefon: "", email: "", segment: "firma", obor: "", velikost: "", zdroj: "reserse", poznamka: "" };

export function FirmaDialog({ open, firma, onClose, onHotovo }) {
  const navigate = useNavigate();
  const [f, setF] = useState(PRAZDNA);
  const [busy, setBusy] = useState(false);
  const [ares, setAres] = useState(false);
  const [chyba, setChyba] = useState("");
  const [aresChyba, setAresChyba] = useState("");
  const [dup, setDup] = useState(null);
  const uprava = !!firma;
  useEffect(() => {
    if (!open) return;
    setChyba(""); setAresChyba(""); setDup(null);
    setF(firma ? Object.fromEntries(Object.keys(PRAZDNA).map((k) => [k, firma[k] ?? PRAZDNA[k]])) : PRAZDNA);
  }, [open, firma]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  // ARES nikdy nebrání uložení: při chybě se jen ukáže hláška a údaje se vyplní ručně.
  const nactiAres = async () => {
    setAres(true); setAresChyba("");
    try {
      const r = await b2bGet("ares", { ico: f.ico });
      if (!r.ok) { setAresChyba(`${r.chyba} Údaje můžeš vyplnit ručně.`); return; }
      setF((p) => ({
        ...p,
        ico: r.ico,
        nazev: p.nazev || r.firma || "",
        dic: r.dic || p.dic,
        adresa: [r.ulice, [r.psc, r.mesto].filter(Boolean).join(" ")].filter(Boolean).join(", ") || p.adresa,
        mesto: r.mesto || p.mesto,
      }));
    } catch (e) { setAresChyba(`${e.message} Údaje můžeš vyplnit ručně.`); }
    finally { setAres(false); }
  };

  const uloz = async () => {
    setBusy(true); setChyba(""); setDup(null);
    try {
      const r = uprava ? await b2bPost("firma_upravit", { id: firma.id, ...f }) : await b2bPost("firma_zalozit", f);
      toast(uprava ? "Údaje uloženy." : "Firma založena.", "success");
      onHotovo?.(r.firma);
    } catch (e) {
      setChyba(e.message);
      if (e.duplicita) setDup(e.duplicita);
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} size="lg" plnaHlavicka
      header={<HlavickaOkna ikona={Building2} nazev={uprava ? "Upravit údaje" : "Nová firma"} popis={uprava ? firma.nazev : "Zadej IČO a údaje se dotáhnou z ARES"} onClose={onClose} />}
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose}>Zrušit</button>
        <button type="button" className="btn-primary" onClick={uloz} disabled={busy || !f.nazev.trim()} aria-busy={busy}>{uprava ? "Uložit" : "Založit firmu"}</button>
      </>}>
      <div className="space-y-4">
        {chyba && (
          <Hlaska ton="problem" title={dup ? "Firma už je v CRM" : "Nepovedlo se uložit"}
            akce={dup ? <button type="button" className="btn-secondary btn-sm" onClick={() => { onClose(); navigate(`/b2b/firma/${dup.id}`); }}>Otevřít firmu</button> : null}>
            {chyba}
          </Hlaska>
        )}
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
          <Field label="IČO"><input className="field" inputMode="numeric" value={f.ico} onChange={set("ico")} placeholder="8 číslic" /></Field>
          <button type="button" className="btn-secondary" onClick={nactiAres} disabled={ares || f.ico.replace(/\D/g, "").length < 6}>
            {ares ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Načíst z ARES
          </button>
        </div>
        {aresChyba && <Hlaska ton="pozor" title="ARES neodpověděl">{aresChyba}</Hlaska>}
        <Field label="Název firmy"><input className="field" value={f.nazev} onChange={set("nazev")} /></Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <RozbalovaciVyber stitek="Segment" aria-label="Segment" value={f.segment} onChange={(v) => setF((p) => ({ ...p, segment: v }))} items={items(SEGMENTY)} />
          <RozbalovaciVyber stitek="Zdroj" aria-label="Zdroj" value={f.zdroj} onChange={(v) => setF((p) => ({ ...p, zdroj: v }))} items={items(ZDROJE, (v) => v)} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Web"><input className="field" value={f.web} onChange={set("web")} placeholder="firma.cz" /></Field>
          <Field label="E-mail firmy"><input className="field" type="email" value={f.email} onChange={set("email")} /></Field>
          <Field label="Telefon firmy"><input className="field" type="tel" value={f.telefon} onChange={set("telefon")} /></Field>
          <RozbalovaciVyber stitek="Velikost" aria-label="Velikost" value={f.velikost} onChange={(v) => setF((p) => ({ ...p, velikost: v }))}
            items={[{ key: "", label: "Neuvedeno" }, ...VELIKOSTI.map((v) => ({ key: v, label: `${v} zaměstnanců` }))]} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Město"><input className="field" value={f.mesto} onChange={set("mesto")} /></Field>
          <Field label="Region"><input className="field" value={f.region} onChange={set("region")} placeholder="například Praha" /></Field>
          <Field label="Adresa" className="sm:col-span-2"><input className="field" value={f.adresa} onChange={set("adresa")} /></Field>
          <Field label="DIČ"><input className="field" value={f.dic} onChange={set("dic")} /></Field>
          <Field label="Obor"><input className="field" value={f.obor} onChange={set("obor")} /></Field>
        </div>
        <Field label="Poznámka"><textarea className="field" rows={2} value={f.poznamka} onChange={set("poznamka")} /></Field>
      </div>
    </Modal>
  );
}

/* --- Změna stavu ---------------------------------------------------------- */
export function StavDialog({ open, firma, vychozi, onClose, onHotovo }) {
  const [stav, setStav] = useState("");
  const [datum, setDatum] = useState("");
  const [duvod, setDuvod] = useState("");
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  useEffect(() => {
    if (!open || !firma) return;
    setChyba(""); setDuvod(""); setDatum(firma.odlozeno_do || "");
    setStav(vychozi || firma.stav);
  }, [open, firma, vychozi]);
  if (!firma) return null;
  const ulozit = async () => {
    setChyba("");
    const popis = stav === "odlozeno" ? ` Vrátí se do fronty Dnes ${dmr(datum)}.` : stav === "ztraceno" ? ` Důvod: ${duvod.trim()}.` : "";
    const ok = await confirmDialog({
      title: "Změnit stav firmy?",
      description: `${firma.nazev}: ${STAVY[firma.stav].label} → ${STAVY[stav].label}.${popis} Změna se zapíše do časové osy.`,
      confirmLabel: "Změnit stav", variant: "primary",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await b2bPost("stav", { id: firma.id, stav, odlozeno_do: stav === "odlozeno" ? datum : undefined, duvod: stav === "ztraceno" ? duvod : undefined });
      toast("Stav změněn.", "success");
      onHotovo?.(r.firma);
    } catch (e) { setChyba(e.message); }
    finally { setBusy(false); }
  };
  const chybi = (stav === "odlozeno" && !datum) || (stav === "ztraceno" && !duvod.trim()) || (stav === firma.stav && stav !== "odlozeno");
  return (
    <Modal open={open} onClose={onClose} size="sm" plnaHlavicka
      header={<HlavickaOkna ikona={stav === "odlozeno" ? CalendarClock : ArrowRightLeft} nazev={stav === "odlozeno" ? "Odložit firmu" : "Změnit stav"} popis={firma.nazev} onClose={onClose} />}
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose}>Zrušit</button>
        <button type="button" className="btn-primary" onClick={ulozit} disabled={busy || chybi} aria-busy={busy}>Pokračovat</button>
      </>}>
      <div className="space-y-4">
        {chyba && <Hlaska ton="problem">{chyba}</Hlaska>}
        <RozbalovaciVyber stitek="Stav" aria-label="Nový stav" value={stav} onChange={setStav} items={items(STAVY)} />
        {stav === "odlozeno" && (
          <div>
            <span className="label">Vrátit do fronty Dnes</span>
            <DatePicker value={datum} min={dnesIsoPraha()} onChange={setDatum} />
          </div>
        )}
        {stav === "ztraceno" && (
          <Field label="Důvod ztráty"><textarea className="field" rows={2} value={duvod} onChange={(e) => setDuvod(e.target.value)} placeholder="například rozpočet na rok vyčerpán" /></Field>
        )}
      </div>
    </Modal>
  );
}

/* --- Další krok ------------------------------------------------------------ */
const CASY = (() => { const o = [{ key: "", label: "bez času" }]; for (let m = 7 * 60; m <= 20 * 60; m += 30) { const c = `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`; o.push({ key: c, label: c }); } return o; })();

export function KrokDialog({ open, firma, onClose, onHotovo }) {
  const [k, setK] = useState({ dalsi_krok: "", dalsi_krok_datum: "", dalsi_krok_typ: "jine", dalsi_krok_cas: "" });
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  useEffect(() => {
    if (!open || !firma) return;
    setChyba("");
    setK({ dalsi_krok: firma.dalsi_krok || "", dalsi_krok_datum: firma.dalsi_krok_datum || "", dalsi_krok_typ: firma.dalsi_krok_typ || "jine", dalsi_krok_cas: firma.dalsi_krok_cas || "" });
  }, [open, firma]);
  if (!firma) return null;
  const uloz = async (smazat = false) => {
    setBusy(true); setChyba("");
    try {
      const r = await b2bPost("dalsi_krok", { id: firma.id, ...(smazat ? { dalsi_krok: "" } : k) });
      toast(smazat ? "Další krok zrušen." : "Další krok uložen.", "success");
      onHotovo?.(r.firma);
    } catch (e) { setChyba(e.message); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} size="sm" plnaHlavicka
      header={<HlavickaOkna ikona={CalendarClock} nazev="Další krok" popis={firma.nazev} onClose={onClose} />}
      footer={<>
        {firma.dalsi_krok && <button type="button" className="btn-secondary mr-auto" onClick={() => uloz(true)} disabled={busy}>Zrušit krok</button>}
        <button type="button" className="btn-secondary" onClick={onClose}>Zpět</button>
        <button type="button" className="btn-primary" onClick={() => uloz(false)} disabled={busy || !k.dalsi_krok.trim() || !k.dalsi_krok_datum} aria-busy={busy}>Uložit</button>
      </>}>
      <div className="space-y-4">
        {chyba && <Hlaska ton="problem">{chyba}</Hlaska>}
        <Field label="Co se má stát"><input className="field" value={k.dalsi_krok} onChange={(e) => setK((p) => ({ ...p, dalsi_krok: e.target.value }))} placeholder="například Zavolat ohledně besedy" /></Field>
        <RozbalovaciVyber stitek="Typ" aria-label="Typ kroku" value={k.dalsi_krok_typ} onChange={(v) => setK((p) => ({ ...p, dalsi_krok_typ: v }))} items={items(TYPY_KROKU, (v) => v)} />
        <div>
          <span className="label">Kdy</span>
          <DatePicker value={k.dalsi_krok_datum} min={dnesIsoPraha()} onChange={(v) => setK((p) => ({ ...p, dalsi_krok_datum: v }))} />
        </div>
        <RozbalovaciVyber stitek="Čas" aria-label="Čas" value={k.dalsi_krok_cas} onChange={(v) => setK((p) => ({ ...p, dalsi_krok_cas: v }))} items={CASY} />
      </div>
    </Modal>
  );
}

/* --- Osoba ------------------------------------------------------------------ */
export function OsobaDialog({ open, firma, osoba, onClose, onHotovo }) {
  const [o, setO] = useState({ jmeno: "", osloveni: "", role: "", email: "", telefon: "", hlavni: false, nekontaktovat: false });
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  useEffect(() => {
    if (!open) return;
    setChyba("");
    setO(osoba ? { jmeno: osoba.jmeno || "", osloveni: osoba.osloveni || "", role: osoba.role || "", email: osoba.email || "", telefon: osoba.telefon || "", hlavni: !!osoba.hlavni, nekontaktovat: !!osoba.nekontaktovat }
      : { jmeno: "", osloveni: "", role: "", email: "", telefon: "", hlavni: false, nekontaktovat: false });
  }, [open, osoba]);
  if (!firma) return null;
  const set = (k) => (e) => setO((p) => ({ ...p, [k]: e.target.value }));
  const uloz = async () => {
    setBusy(true); setChyba("");
    try {
      if (osoba) await b2bPost("osoba_upravit", { id: osoba.id, ...o });
      else await b2bPost("osoba_pridat", { firma_id: firma.id, ...o });
      toast(osoba ? "Osoba uložena." : "Osoba přidána.", "success");
      onHotovo?.();
    } catch (e) { setChyba(e.message); }
    finally { setBusy(false); }
  };
  const smaz = async () => {
    const ok = await confirmDialog({ title: "Smazat osobu?", description: `${osoba.jmeno} se odebere z firmy. Historie v časové ose zůstane.`, confirmLabel: "Smazat", variant: "danger" });
    if (!ok) return;
    setBusy(true);
    try { await b2bPost("osoba_smazat", { id: osoba.id }); toast("Osoba smazána.", "success"); onHotovo?.(); }
    catch (e) { setChyba(e.message); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} size="md" plnaHlavicka
      header={<HlavickaOkna ikona={UserPlus} nazev={osoba ? "Upravit osobu" : "Přidat osobu"} popis={firma.nazev} onClose={onClose} />}
      footer={<>
        {osoba && <button type="button" className="btn-secondary mr-auto" onClick={smaz} disabled={busy}><Trash2 className="h-4 w-4" />Smazat</button>}
        <button type="button" className="btn-secondary" onClick={onClose}>Zrušit</button>
        <button type="button" className="btn-primary" onClick={uloz} disabled={busy || !o.jmeno.trim()} aria-busy={busy}>Uložit</button>
      </>}>
      <div className="space-y-4">
        {chyba && <Hlaska ton="problem">{chyba}</Hlaska>}
        <Field label="Jméno a příjmení"><input className="field" value={o.jmeno} onChange={set("jmeno")} /></Field>
        <Field label="Role ve firmě"><input className="field" value={o.role} onChange={set("role")} placeholder="například HR a benefity" /></Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="E-mail"><input className="field" type="email" value={o.email} onChange={set("email")} /></Field>
          <Field label="Telefon"><input className="field" type="tel" value={o.telefon} onChange={set("telefon")} /></Field>
        </div>
        <Field label="Oslovení (5. pád)" hint="Prázdné se doplní samo, například paní Dvořáková nebo pane Nováku.">
          <input className="field" value={o.osloveni} onChange={set("osloveni")} />
        </Field>
        <label className="flex min-h-[44px] items-center gap-3 text-sm text-slate-800">
          <input type="checkbox" className="h-5 w-5" checked={o.hlavni} onChange={(e) => setO((p) => ({ ...p, hlavni: e.target.checked }))} />Hlavní kontakt
        </label>
        {osoba && (
          <label className="flex min-h-[44px] items-center gap-3 text-sm text-slate-800">
            <input type="checkbox" className="h-5 w-5" checked={o.nekontaktovat} onChange={(e) => setO((p) => ({ ...p, nekontaktovat: e.target.checked }))} />Nekontaktovat tuto osobu
          </label>
        )}
      </div>
    </Modal>
  );
}
